import { useCallback, useMemo, useState, type ReactNode } from 'react'
import type { ChartData, ChartOptions, Plugin } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ISensemakerOverviewInstrumentSplit } from '@brushfire/types'
import '@/features/campaigns/charts/registerCharts'
import {
  SENSEMAKER_OVERVIEW_SPLIT_CHART_BORDER,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_BODY_GAP_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_SIZE_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_OFFSET_LEFT_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_CUTOUT,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_PADDING_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_RADIUS_PX,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_TITLE_COLOR,
  SENSEMAKER_OVERVIEW_SPLIT_CHART_TITLE_TO_BODY_GAP_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_ROW_GAP_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_RADIUS_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_TO_LABEL_GAP_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_WIDTH_PX,
  SENSEMAKER_OVERVIEW_SPLIT_LEGEND_TEXT_COLOR,
  SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_COLORS,
  SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_DIM_OPACITY,
} from '../../lib/sensemaker-overview-layout'

const SEGMENT_LABELS = [
  'Manual Instrument Builder',
  'Instrument Library',
  'AI Instrument Builder',
] as const

function fadeSplitSegmentColor(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) return hex
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  if ([r, g, b].some((v) => Number.isNaN(v))) return hex
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function createInstrumentSplitCenterPlugin(total: number): Plugin<'doughnut'> {
  return {
    id: 'sensemakerInstrumentSplitCenter',
    beforeDraw(chart) {
      const { ctx, chartArea } = chart
      if (!chartArea) return
      const centerX = (chartArea.left + chartArea.right) / 2
      const centerY = (chartArea.top + chartArea.bottom) / 2
      ctx.save()
      ctx.font = 'bold 18.581px "Plus Jakarta Sans", Inter, sans-serif'
      ctx.fillStyle = '#000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(total), centerX, centerY)
      ctx.restore()
    },
  }
}

interface Props {
  split: ISensemakerOverviewInstrumentSplit
}

function ChartCardShell({
  children,
  testId,
}: Readonly<{ children: ReactNode; testId: string }>) {
  return (
    <Box
      sx={{
        border: SENSEMAKER_OVERVIEW_SPLIT_CHART_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_RADIUS_PX}px`,
        p: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_PADDING_PX}px`,
        bgcolor: '#fff',
        height: SENSEMAKER_OVERVIEW_SPLIT_CHART_HEIGHT_PX,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid={testId}
    >
      <Box sx={{ flexShrink: 0, mb: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_TITLE_TO_BODY_GAP_PX}px` }}>
        <Typography
          sx={{
            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: 'normal',
            color: SENSEMAKER_OVERVIEW_SPLIT_CHART_TITLE_COLOR,
          }}
        >
          Instrument Type Split
        </Typography>
      </Box>
      {children}
    </Box>
  )
}

export function SensemakerInstrumentSplitChart({ split }: Readonly<Props>) {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null)
  const segments = [split.manual, split.library, split.ai]
  const total = split.total

  const handleChartHover = useCallback(
    (_event: unknown, elements: { index?: number }[]) => {
      const nextIndex = elements[0]?.index ?? null
      setHoveredSegment((prev) => (prev === nextIndex ? prev : nextIndex))
    },
    [],
  )

  const handleChartLeave = useCallback(() => {
    setHoveredSegment(null)
  }, [])

  const handleLegendEnter = useCallback((index: number) => {
    setHoveredSegment(index)
  }, [])

  const handleLegendLeave = useCallback(() => {
    setHoveredSegment(null)
  }, [])

  const handleLegendClick = useCallback((index: number) => {
    setHoveredSegment(index)
  }, [])

  const segmentColors = useMemo(
    () =>
      SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_COLORS.map((color, i) =>
        hoveredSegment === null || hoveredSegment === i
          ? color
          : fadeSplitSegmentColor(color, SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_DIM_OPACITY),
      ),
    [hoveredSegment],
  )

  const legendItems = useMemo(
    () =>
      SEGMENT_LABELS.map((label, i) => {
        const count = segments[i] ?? 0
        const pct = total > 0 ? Math.round((count / total) * 100) : 0
        return { label, count, pct, color: SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_COLORS[i] }
      }),
    [segments, total],
  )

  const data = useMemo<ChartData<'doughnut'>>(
    () => ({
      labels: [...SEGMENT_LABELS],
      datasets: [
        {
          data: segments,
          backgroundColor: segmentColors,
          hoverBackgroundColor: [...SENSEMAKER_OVERVIEW_SPLIT_SEGMENT_COLORS],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [segmentColors, segments],
  )

  const options = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_CUTOUT,
      onHover: handleChartHover,
      onLeave: handleChartLeave,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#fff',
          borderColor: '#f47937',
          borderWidth: 1.5,
          titleColor: '#6b7280',
          bodyColor: '#000',
          titleFont: { family: 'Inter, sans-serif', size: 12, weight: 'normal' },
          bodyFont: { family: '"Plus Jakarta Sans", Inter, sans-serif', size: 12, weight: 'bold' },
          padding: 10,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0]?.dataIndex
              return typeof index === 'number' ? SEGMENT_LABELS[index] : ''
            },
            label: (ctx) => {
              const pct = total > 0 ? Math.round((ctx.parsed / total) * 100) : 0
              return `${ctx.parsed} (${pct}%)`
            },
          },
        },
      },
    }),
    [handleChartHover, handleChartLeave, total],
  )

  const centerPlugin = useMemo(() => createInstrumentSplitCenterPlugin(total), [total])

  if (total === 0) {
    return (
      <ChartCardShell testId="sensemaker-instrument-split-chart">
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: '#64748b',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          No instruments yet.
        </Typography>
      </ChartCardShell>
    )
  }

  return (
    <ChartCardShell testId="sensemaker-instrument-split-chart">
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          gap: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_BODY_GAP_PX}px`,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            width: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_SIZE_PX}px`,
            height: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_SIZE_PX}px`,
            maxWidth: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_SIZE_PX}px`,
            flexShrink: 0,
            position: 'relative',
            ml: `${SENSEMAKER_OVERVIEW_SPLIT_CHART_DONUT_OFFSET_LEFT_PX}px`,
          }}
        >
          <Doughnut data={data} options={options} plugins={[centerPlugin]} />
        </Box>

        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: `${SENSEMAKER_OVERVIEW_SPLIT_LEGEND_ROW_GAP_PX}px`,
            justifyContent: 'center',
            alignSelf: 'center',
            py: 1,
            pr: 0.5,
          }}
          data-testid="sensemaker-instrument-split-legend"
        >
          {legendItems.map((item, index) => {
            const isActive = hoveredSegment === null || hoveredSegment === index
            return (
            <Box
              key={item.label}
              role="button"
              tabIndex={0}
              onMouseEnter={() => handleLegendEnter(index)}
              onMouseLeave={handleLegendLeave}
              onClick={() => handleLegendClick(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleLegendClick(index)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: `${SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_TO_LABEL_GAP_PX}px`,
                minHeight: '16px',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.45,
                transition: 'opacity 0.15s ease',
              }}
            >
              <Box
                sx={{
                  width: `${SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_WIDTH_PX}px`,
                  height: `${SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_HEIGHT_PX}px`,
                  borderRadius: `${SENSEMAKER_OVERVIEW_SPLIT_LEGEND_SWATCH_RADIUS_PX}px`,
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '15.986px',
                  color: SENSEMAKER_OVERVIEW_SPLIT_LEGEND_TEXT_COLOR,
                  fontFamily: 'Inter, sans-serif',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label} ({item.pct}%)
              </Typography>
            </Box>
            )
          })}
        </Box>
      </Box>
    </ChartCardShell>
  )
}
